import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from "vue";
import { useEUAccountsStore } from "@/store_v3/EUAccountsStore";
import { storeToRefs } from "pinia";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import ComponentHeader from "./ComponentHeader.vue";
import BaseUpload from "@/components-UI/BaseUpload.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseTextarea from "@/components-UI/BaseTextarea.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import ComponentServicesTabStats from "./ComponentServicesTabStats.vue";
import { addFile, deleteFile, downloadFile as downloadFileFromApi } from "@/api";
import { postStatTaskComment } from "@/api/revenue";
import { IEUAccountStats, IFile } from "@/api/types/revenue";
import router from "@/router";
import store from "@/store";
import { showServerError } from "@/utils";
import BasePageTabs from "@/components-UI/BasePageTabs.vue";
import SvgDownload from "@/assets/icons/download-uploaded.svg";
import ViewDateTime from "@/components/base/ViewDateTime.vue";
import SvgSend from "@/assets/icons/send.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import BaseCollapse from "@/components-UI/BaseCollapse.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentTabStats',
  setup(__props) {

const EUAccountsStore = useEUAccountsStore();
const {
    form,
    EUAccountObj,
    isEdit,
} = storeToRefs(EUAccountsStore);

const isFirstTableFilled = computed(() => (form.value.stats?.[0]?.services?.length ?? 0) > 0);

const isSingle = computed(() => router.currentRoute.params.id !== 'create');
const hasStatsTask = computed(() => Boolean(EUAccountObj.value?.stats));
const isStatsCreationMode = computed(() => !isSingle.value || EUAccountObj.value?.stats === null);

const ALL_FILES_TAB_VALUE = 'all';
const NO_FILE_TAB_VALUE = 'no-file';
const activeTabValue = ref(ALL_FILES_TAB_VALUE);

const hasFile = (item: IEUAccountStats): boolean => {
    const fileObj = item?.file;
    if (fileObj && typeof fileObj === 'object' && (fileObj as { name?: string }).name) return true;
    if (typeof fileObj === 'number') return true;
    return !!(item?.tempFile?.length);
};

const getFileDisplayName = (item: IEUAccountStats): string | null => {
    if (item?.tempFile?.length) return item.tempFile[0].name ?? null;
    const fileObj = item?.file as { name?: string } | number | null | undefined;
    if (fileObj && typeof fileObj === 'object' && fileObj.name) return fileObj.name;
    if (typeof fileObj === 'number') return 'Файл';
    return null;
};

const getFileLabel = (item: IEUAccountStats): string => {
    const t = item?.tempFile?.[0];
    if (t?.name != null) {
        const typeOrExt = (t as File & { type?: string }).type;
        const isExtension = typeOrExt && !typeOrExt.includes('/');
        if (isExtension && !t.name.toLowerCase().endsWith('.' + typeOrExt.toLowerCase())) {
            return `${t.name}.${typeOrExt}`;
        }
        return t.name;
    }
    return getFileDisplayName(item) ?? 'Файл';
};

const getTabs = computed(() => {
    const stats = form.value.stats ?? [];
    const tabs: { text: string; value: string }[] = [{ text: 'Все файлы', value: ALL_FILES_TAB_VALUE }];
    const fileNames = new Set<string>();
    let hasNoFile = false;
    stats.forEach((el) => {
        if (hasFile(el)) {
            const name = getFileLabel(el);
            if (name && name !== 'Файл') fileNames.add(name);
        } else {
            hasNoFile = true;
        }
    });
    fileNames.forEach((name) => {
        tabs.push({ text: name, value: name });
    });
    if (hasNoFile) {
        tabs.push({ text: 'Без файла', value: NO_FILE_TAB_VALUE });
    }
    return tabs;
});

const filteredStats = computed(() => {
    const stats = form.value.stats ?? [];
    if (!isSingle.value || !stats.length) {
        return stats.map((item, index) => ({ item, index }));
    }
    if (activeTabValue.value === ALL_FILES_TAB_VALUE) {
        return stats.map((item, index) => ({ item, index }));
    }
    if (activeTabValue.value === NO_FILE_TAB_VALUE) {
        return stats
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => !hasFile(item));
    }
    const fileName = activeTabValue.value;
    return stats
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => hasFile(item) && getFileLabel(item) === fileName);
});

watch(
    getTabs,
    (tabs) => {
        if (activeTabValue.value === ALL_FILES_TAB_VALUE || activeTabValue.value === NO_FILE_TAB_VALUE) return;
        const exists = tabs.some((t) => t.value === activeTabValue.value);
        if (!exists) {
            activeTabValue.value = ALL_FILES_TAB_VALUE;
        }
    },
    { deep: true },
);

const getFileMeta = (item: IEUAccountStats): IFile | null => {
    if (typeof item?.file !== 'number') return null;
    const t = item?.tempFile?.[0] as { name?: string; type?: string } | undefined;
    if (t?.name != null) {
        const ext = t.type && !t.type.includes('/') ? t.type : '';
        const baseName = ext && t.name.toLowerCase().endsWith('.' + ext.toLowerCase())
            ? t.name.slice(0, -ext.length - 1) : t.name;
        return { id: item.file, original_name: baseName, extension: ext };
    }
    const fileName = getFileDisplayName(item) || 'file';
    const lastDot = fileName.lastIndexOf('.');
    return {
        id: item.file,
        original_name: lastDot >= 0 ? fileName.slice(0, lastDot) : fileName,
        extension: lastDot >= 0 ? fileName.slice(lastDot + 1) : '',
    };
};

const handleDownload = async (item: IEUAccountStats): Promise<void> => {
    const fileMeta = getFileMeta(item);
    if (!fileMeta?.id) return;
    try {
        await downloadFileFromApi(fileMeta);
    } catch (e) {
        showServerError(e, 'Не удалось скачать файл');
    }
};

const handleFileUpdate = async (item: IEUAccountStats, event: File[] | []): Promise<void> => {
    try {
        if (event.length) {
            const resp = await addFile(event[0] as unknown as IFile);
            item.file = resp.data.data.id;
        } else {
            if (typeof item.file === 'number') {
                await deleteFile(item.file);
            }
            item.file = null;
        }
        item.tempFile = event;
    } catch (e) {
        showServerError(e, 'Не удалось загрузить файл');
    }
};

const formatCommentDate = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

const handleSendComment = async (item: IEUAccountStats & { id?: number; comments?: Array<{ created_at: string; user_name: string; text: string }> }): Promise<void> => {
    const text = (item.comment ?? '').trim();
    if (!text) return;
    const statTaskId = EUAccountObj.value?.stats?.id;
    const itemId = item.id;
    if (statTaskId == null || itemId == null) return;
    try {
        await postStatTaskComment(statTaskId, itemId, { text });
        const currentUser = store.getters['authModule/GET_USER'];
        const user_name = currentUser?.first_name && currentUser?.last_name
            ? `${currentUser.first_name} ${currentUser.last_name}`
            : (currentUser?.full_name ?? '');
        const newComment = {
            created_at: formatCommentDate(),
            user_name,
            text,
        };
        if (!item.comments) item.comments = [];
        item.comments.push(newComment);
        item.comment = '';
    } catch (e) {
        showServerError(e, 'Не удалось отправить комментарий');
    }
};

const handleRemoveBlock = (index: number): void => {
    form.value.stats?.splice(index, 1);
};

const handleAddMore = (): void => {
    form.value.stats.push({
        // Новый блок ещё не существует на бэке: порядковый номер здесь читался бы
        // как идентификатор записи, и пустой блок уходил бы в сохранение
        id: 0,
        services: [],
        tempFile: [],
        file: null,
        comment: '',
    });
};

return { __sfc: true,EUAccountsStore, form, EUAccountObj, isEdit, isFirstTableFilled, isSingle, hasStatsTask, isStatsCreationMode, ALL_FILES_TAB_VALUE, NO_FILE_TAB_VALUE, activeTabValue, hasFile, getFileDisplayName, getFileLabel, getTabs, filteredStats, getFileMeta, handleDownload, handleFileUpdate, formatCommentDate, handleSendComment, handleRemoveBlock, handleAddMore, BaseEditableCard, ComponentHeader, BaseUpload, BaseList, BaseListItem, BaseTextarea, BaseButton, ComponentServicesTabStats, BasePageTabs, SvgDownload, ViewDateTime, SvgSend, SvgTrash, BaseCollapse }
}

})